<template>
  <div>
    <!-- Dialog de Detalhes (lista) — oculto no modo openDirectAdd -->
    <v-dialog
      v-if="!openDirectAdd"
      v-model="internalValue"
      transition="dialog-bottom-transition"
      max-width="1000px"
      scrollable
    >
      <v-card class="ds-dialog">
        <v-card-title
          class="ds-dialog-header d-flex justify-space-between align-center"
          :style="{ background: theme.main }"
        >
          <div class="d-flex align-center">
            <v-icon color="white" class="mr-2">{{ theme.icon }}</v-icon>
            <span class="ds-dialog-title">{{ dialogTitle }}</span>
          </div>
          <div class="d-flex align-center">
            <v-tooltip v-if="!readonly" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon color="white" @click="openAddDialog" class="mr-1" v-bind="attrs" v-on="on">
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>
              <span>Adicionar</span>
            </v-tooltip>
            <v-btn icon color="white" class="close-btn" @click="closeDetailsDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <section v-if="conditions.length" class="ds-form-section ds-form-section--flush">
            <v-simple-table class="history-table">
              <thead>
                <tr>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4">Descrição</th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4" style="width: 160px">Data</th>
                  <th v-if="!readonly" class="text-left text-uppercase caption font-weight-bold pa-4" style="width: 90px">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in conditions" :key="item.id" class="history-row">
                  <td class="pa-4">
                    <strong v-if="item.code" class="mr-1">{{ item.code }}</strong>
                    <span class="body-2 font-weight-medium">{{ item.description }}</span>
                  </td>
                  <td class="text-no-wrap pa-4">
                    <div class="d-flex align-center">
                      <v-icon small color="grey" class="mr-2">mdi-calendar</v-icon>
                      <span class="body-2">{{ formatDate(item.created_at) }}</span>
                    </div>
                  </td>
                  <td v-if="!readonly" class="pa-4">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon x-small color="error" class="action-btn" @click="confirmDelete(item)" v-bind="attrs" v-on="on">
                          <v-icon small>mdi-trash-can-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>Excluir</span>
                    </v-tooltip>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </section>

          <section v-else class="ds-form-section text-center py-8 px-4">
            <v-icon size="56" color="grey lighten-1">{{ theme.icon }}</v-icon>
            <p class="body-1 grey--text mt-3 mb-0">Nada registrado.</p>
            <p v-if="readonly" class="caption grey--text mt-1 mb-0">
              Adicione pelo card de CIDs (escolhendo a categoria).
            </p>
            <v-btn v-else :color="theme.main" outlined small class="mt-4" @click="openAddDialog">
              <v-icon small left>mdi-plus</v-icon>
              Adicionar primeiro
            </v-btn>
          </section>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog de Adicionar -->
    <v-dialog v-model="formDialogVisible" max-width="600px" scrollable>
      <v-card class="ds-dialog">
        <v-card-title
          class="ds-dialog-header d-flex justify-space-between align-center"
          :style="{ background: theme.main }"
        >
          <div class="d-flex align-center" style="position: relative; z-index: 1">
            <v-icon color="white" class="mr-2">mdi-plus-circle-outline</v-icon>
            <span class="ds-dialog-title">Adicionar — {{ dialogTitle }}</span>
          </div>
          <v-btn icon color="white" class="close-btn" @click="closeFormDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-clipboard-pulse-outline</v-icon>
                <span>CID</span>
              </div>
            </div>
            <!--
              salvia-770: busca no catálogo de CID e, se nada for encontrado, o
              médico digita e o item entra assim mesmo. A categoria não é escolha
              dele aqui — vem do card (Doença crônica, Transtorno) — e o seletor
              aparece preenchido e desabilitado, para deixar à vista em que
              categoria o registro vai entrar.

              `existing` são as condições já gravadas do paciente: é o que impede
              cadastrar a mesma doença de novo a cada reabertura do diálogo.
            -->
            <CidField
              :key="'cond-' + formRenderKey"
              v-model="selectedItems"
              :fixed-category="category"
              :existing="conditions"
            />
          </section>
        </v-card-text>

        <v-card-actions class="ds-dialog-actions">
          <v-btn outlined color="primary" @click="closeFormDialog">
            <v-icon left>mdi-close</v-icon>
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            :color="theme.main"
            dark
            :disabled="!canSave"
            :loading="formLoading"
            depressed
            class="px-6"
            @click="saveData"
          >
            <v-icon small left>mdi-plus</v-icon>
            Adicionar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { useSnackbar } from "@/composables/useSnackbar";
import CidField from "@/features/patients/profile-dashboard/fields/CidField.vue";
import { createConditions, deleteCondition } from "@/services/conditionService";
import { formatDateTime } from "@/utils/datetime";

export default {
  name: "EditConditionsDialog",

  components: { CidField },

  props: {
    value: { type: Boolean, default: false },
    dialogTitle: { type: String, default: "Condições" },
    conditions: { type: Array, default: () => [] },
    patientId: { type: [String, Number], required: true },
    category: { type: String, default: "chronic" },
    mode: { type: String, default: "text" }, // 'catalog' | 'text'
    theme: {
      type: Object,
      default: () => ({ main: "#546E7A", icon: "mdi-clipboard-text-outline" }),
    },
    // Só leitura (cards de crônicas/transtornos): sem adicionar e sem excluir.
    readonly: { type: Boolean, default: false },
    openDirectAdd: { type: Boolean, default: false },
  },

  setup() {
    const { success, error, warning, erroDeRequisicao } = useSnackbar();
    return { success, error, warning, erroDeRequisicao };
  },

  data() {
    return {
      formDialogVisible: false,
      selectedItems: [],
      // Trocar esta chave recria o CidField. Limpar `selectedItems` nao
      // bastava: o texto ja digitado na CAIXA DE BUSCA vive dentro do campo, e
      // reaparecia ao abrir o dialogo de novo.
      formRenderKey: 0,
      formLoading: false,
      itemToDelete: null,
    };
  },

  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    canSave() {
      if (this.formLoading) return false;
      return this.selectedItems.length > 0;
    },
  },

  watch: {
    value(val) {
      if (val && this.openDirectAdd) {
        this.$nextTick(() => this.openAddDialog());
      }
    },
    formDialogVisible(val) {
      if (!val && this.openDirectAdd) {
        this.$emit("input", false);
      }
    },
  },

  methods: {
    formatDate: formatDateTime,
    closeDetailsDialog() {
      this.$emit("input", false);
    },

    openAddDialog() {
      if (this.readonly) return;
      this.selectedItems = [];
      this.formRenderKey += 1;
      this.formDialogVisible = true;
    },

    closeFormDialog() {
      this.formDialogVisible = false;
      this.selectedItems = [];
    },

    buildConditions() {
      // Os itens vêm do CidField: os do catálogo trazem código e os digitados
      // vêm com `code: null`. A categoria já chega fixa pelo card, mas é
      // reafirmada aqui — o back é a fronteira que vale, e um item sem
      // categoria seria recusado com 422.
      return this.selectedItems
        .map((item) => ({
          description: String(item.description || "").trim(),
          code: item.code || null,
          description_abbrev: item.description_abbrev || null,
          parent_code: item.parent_code || null,
          category: this.category,
          is_chronic: this.category === "chronic",
        }))
        .filter((c) => c.description !== "");
    },

    async saveData() {
      const conditions = this.buildConditions();
      if (conditions.length === 0) {
        this.warning("Adicione ao menos um item");
        return;
      }
      this.formLoading = true;
      try {
        await createConditions(this.patientId, conditions);
        // salvia-781: guarda antes de fechar o form, que limpa a selecao.
        const registrado = this.selectedItems;
        this.success("Adicionado com sucesso!");
        this.closeFormDialog();
        this.$emit("data-saved", registrado);
      } catch (err) {
        console.error("Erro ao salvar condições:", err);
        this.erroDeRequisicao(err, err.response?.data?.message || "Erro ao salvar");
      } finally {
        this.formLoading = false;
      }
    },

    async confirmDelete(item) {
      this.itemToDelete = item;
      const ok = await this.$confirm({
        type: "delete",
        title: "Confirmar exclusão",
        message: `Tem certeza que deseja remover "${item.description}"?`,
        hint: "Esta ação não poderá ser desfeita.",
      });
      if (!ok) {
        this.itemToDelete = null;
        return;
      }
      await this.deleteData();
    },

    async deleteData() {
      if (!this.itemToDelete) return;
      try {
        await deleteCondition(this.itemToDelete.id);
        this.success("Removido com sucesso!");
        this.itemToDelete = null;
        this.$emit("data-deleted");
      } catch (err) {
        console.error("Erro ao excluir:", err);
        this.erroDeRequisicao(err, err.response?.data?.message || "Erro ao remover");
      }
    },
  },
};
</script>

<style scoped>
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18) !important;
}

.ds-dialog-header {
  color: var(--ds-white);
}

.ds-dialog-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.ds-dialog-header :deep(.v-btn) {
  position: relative;
  z-index: 1;
}

.ds-dialog-header :deep(.v-btn:hover) {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

.history-table thead th {
  background-color: var(--ds-surface) !important;
  border-bottom: 2px solid #eeeeee !important;
  letter-spacing: 0.4px;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  color: #607d8b !important;
}

.history-row:hover {
  background-color: var(--ds-surface) !important;
}

.history-row td {
  border-bottom: 1px solid #f0f0f0 !important;
  color: var(--ds-text-600);
}

.action-btn {
  transition: transform 0.15s ease;
}
.action-btn:hover {
  transform: scale(1.15);
}
</style>
